import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, FileDown, MapPin } from "lucide-react";
import { WHATSAPP_URL, MAPS_URL, PROFILE_PDF_URL } from "@/constants/links";

const metrics = [
  { value: "±50,000 KL", label: "INSTALLED STORAGE CAPACITY", testId: "metric-capacity" },
  { value: "6", label: "STORAGE TANKS", testId: "metric-tanks" },
  { value: "3 × 15,000 KL", sub: "2 × 2,000 KL · 1 × 1,000 KL", label: "TANK CONFIGURATION", testId: "metric-tank-mix" },
  { value: "PROBOLINGGO", label: "EAST JAVA, INDONESIA", testId: "metric-location" },
];

const EASE = [0.16, 1, 0.3, 1];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  return (
    <section ref={ref} id="home" data-testid="hero-section" className="relative flex min-h-screen flex-col overflow-hidden pt-16 sm:pt-[72px]">
      <div className="absolute inset-0">
        <motion.img
          src="/assets/tank-farm-wide.webp"
          alt="PT Utama Jaya Nitya bulk liquid storage tank and pumping units"
          style={{ y: imageY, scale: 1.15 }}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-ujn-deep/85" />
        <div className="absolute inset-0 bg-gradient-to-t from-ujn-deep via-ujn-deep/40 to-ujn-deep/70" />
      </div>

      <div className="relative mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-5 py-20 lg:px-8">
        <motion.p
          data-testid="hero-eyebrow"
          className="font-mono text-[11px] tracking-[0.28em] text-ujn-subtle sm:text-xs"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
        >
          PT UTAMA JAYA NITYA — PROBOLINGGO, EAST JAVA, INDONESIA
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="mt-6 max-w-4xl font-heading text-4xl font-extrabold leading-[1.02] tracking-tight text-ujn-offwhite sm:text-5xl lg:text-6xl"
        >
          <span className="block overflow-hidden pb-1">
            <motion.span
              className="block"
              initial={{ y: "112%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 0.95, delay: 0.22, ease: EASE }}
            >
              BULK LIQUID STORAGE
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-1">
            <motion.span
              className="block"
              initial={{ y: "112%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 0.95, delay: 0.36, ease: EASE }}
            >
              &amp; HANDLING PARTNER
            </motion.span>
          </span>
        </h1>

        <motion.p
          data-testid="hero-supporting-text"
          className="mt-6 max-w-xl text-base leading-relaxed text-ujn-offwhite/80 md:text-lg"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.62, ease: EASE }}
        >
          Practical storage, handling and logistics support from Probolinggo, East Java, Indonesia.
        </motion.p>
        <motion.p
          data-testid="hero-support-line"
          className="mt-4 font-mono text-[11px] tracking-[0.14em] text-ujn-offwhite/60 sm:text-xs"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.74, ease: EASE }}
        >
          External Storage • Overflow • Buffer • Temporary • Selected Industrial Bulk Liquids
        </motion.p>

        <motion.div
          className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.86, ease: EASE }}
        >
          <motion.a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-button"
            className="inline-flex items-center justify-center gap-2.5 bg-ujn-medium px-7 py-4 font-mono text-xs font-semibold tracking-[0.16em] text-ujn-offwhite transition-colors duration-200 hover:bg-ujn-accent"
            whileHover={{ y: -2 }}
            transition={{ type: "spring", stiffness: 400, damping: 22 }}
          >
            <MessageCircle className="h-4 w-4" />
            DISCUSS YOUR REQUIREMENTS
          </motion.a>
          <motion.a
            href={PROFILE_PDF_URL}
            download="UJN Company Profile.pdf"
            data-testid="hero-company-profile-button"
            className="inline-flex items-center justify-center gap-2.5 border border-ujn-offwhite/40 px-7 py-4 font-mono text-xs font-semibold tracking-[0.16em] text-ujn-offwhite transition-colors duration-200 hover:border-ujn-offwhite hover:bg-ujn-offwhite/5"
            whileHover={{ y: -2 }}
            transition={{ type: "spring", stiffness: 400, damping: 22 }}
          >
            <FileDown className="h-4 w-4" />
            DOWNLOAD COMPANY PROFILE
          </motion.a>
          <motion.a
            href={MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-directions-button"
            className="inline-flex items-center justify-center gap-2.5 px-7 py-4 font-mono text-xs font-semibold tracking-[0.16em] text-ujn-offwhite/75 transition-colors duration-200 hover:text-ujn-offwhite"
            whileHover={{ y: -2 }}
            transition={{ type: "spring", stiffness: 400, damping: 22 }}
          >
            <MapPin className="h-4 w-4" />
            GET DIRECTIONS
          </motion.a>
        </motion.div>
      </div>

      <motion.div
        className="relative border-t border-ujn-offwhite/15 bg-ujn-deeper/90"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.0, ease: EASE }}
      >
        <div
          data-testid="hero-metrics-bar"
          className="mx-auto grid max-w-7xl grid-cols-2 divide-x divide-ujn-offwhite/10 lg:grid-cols-4"
        >
          {metrics.map((m) => (
            <div key={m.testId} data-testid={m.testId} className="px-5 py-6 lg:px-8 lg:py-8">
              <p className="font-heading text-xl font-bold tracking-tight text-ujn-offwhite sm:text-2xl">
                {m.value}
              </p>
              {m.sub && (
                <p className="mt-1 font-mono text-[10px] tracking-[0.14em] text-ujn-offwhite/60">{m.sub}</p>
              )}
              <p className="mt-1.5 font-mono text-[10px] tracking-[0.2em] text-ujn-subtle">{m.label}</p>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
